import React, { useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { CustomModal, Flex, UiButton } from '@shared/ui';

import cls from './AddPartnerModal.module.css';
import { icons } from '../../../../components/icons/Icons';
import { Form } from '../../../../components/UI/Form/Form';
import { FormBlock } from '../../../../components/UI/Form/FormBlock/FormBlock';
import { Select } from '../../../../components/UI/Select/Select';
import { Input } from '../../../../components/UI/TextInput/Input';
import { paymentActions } from '../../../../store/slices/paymentSlice/paymentSlice';
import { fetchCreatePartner, fetchDaData } from '../../lib/model/api/addPaymentsApi';
import { ModalContext } from '../AddPaymentModal/AddPaymentModal';

export const AddPartnerModal = ({ isOpen, onShowModal, setMainForm }) => {
    const { t } = useTranslation();

    const partnersSelectList = useSelector((state) => state.paymentReducer.partnersSelectList);
    const dispatch = useDispatch();

    const options = [
        {
            title: t('AddPartnerModal.options.title.subcontractor'),
            value: 'subcontractor',
            icon: (
                <svg
                    width="8"
                    height="8"
                    viewBox="0 0 8 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <rect width="8" height="8" rx="1" fill="#28B57A" />
                </svg>
            ),
        },
        {
            title: t('AddPartnerModal.options.title.supplier'),
            value: 'supplier',
            icon: (
                <svg
                    width="8"
                    height="8"
                    viewBox="0 0 8 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <rect width="8" height="8" rx="1" fill="#A483EA" />
                </svg>
            ),
        },
        {
            title: t('AddPartnerModal.options.title.customer'),
            value: 'customer',
            icon: (
                <svg
                    width="8"
                    height="8"
                    viewBox="0 0 8 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <rect width="8" height="8" rx="1" fill="#D7A46F" />
                </svg>
            ),
        },
        {
            title: t('AddPartnerModal.options.title.investor'),
            value: 'investor',
            icon: (
                <svg
                    width="8"
                    height="8"
                    viewBox="0 0 8 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <rect width="8" height="8" rx="1" fill="#95BBE9" />
                </svg>
            ),
        },
        {
            title: t('AddPartnerModal.options.title.other'),
            value: 'other',
            icon: (
                <svg
                    width="8"
                    height="8"
                    viewBox="0 0 8 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <rect width="8" height="8" rx="1" fill="#CAC5D2" />
                </svg>
            ),
        },
    ];

    const [dadataList, setDadataList] = useState([]);

    const [formState, setFormState] = useState({
        type: '',
        name: '',
        email: '',
        phone: '',
        fullName: '',
        inn: '',
        kpp: '',
        bik: '',
        currentAccountNumber: '',
    });

    const fullNameHandler = (e) => {
        const { name, value } = e.target;
        setFormState((prevState) => ({ ...prevState, [name]: value }));

        if (value.length >= 3 && isSearchable) {
            const callback = (data) => {
                setDadataList(data.data);
                loader.hide();
            };

            setTimeout(() => {
                loader.show();
                fetchDaData({ name: 'full-name', value }, callback);
            }, 300);
        }
    };

    const [errors, setErrors] = useState({
        type: null,
        email: null,
        name: null,
    });

    const onSubmitButton = () => {
        loader.show();
        const formData = new FormData();

        Object.entries(formState).forEach(([key, value]) => {
            formData.append(key, value);
        });
        formData.append('submit', 1);

        const callback = async (data) => {
            if (data.success) {
                loader.hide();
                const isType = partnersSelectList.findIndex(
                    (type) => type.value === data.partner.type
                );

                let newList = [...partnersSelectList];
                if (isType !== -1) {
                    const findType = partnersSelectList.find(
                        (type) => type.value === data.partner.type
                    );

                    const newChildren = [
                        ...findType.children,
                        {
                            value: data.partner.guid,
                            title: data.partner.name,
                            partner: data.partner,
                        },
                    ];
                    const updateType = { ...findType, children: newChildren };

                    newList.splice(isType + 1, 0, {
                        value: data.partner.guid,
                        title: data.partner.name,
                        partner: data.partner,
                    });
                    newList.splice(isType, 1, updateType);
                } else {
                    const newPartnerType = [
                        {
                            value: data.partner.type,
                            title: data.partner.humanize_type,
                            isHead: true,
                            isDisabled: true,
                            children: [
                                {
                                    value: data.partner.guid,
                                    title: data.partner.name,
                                    partner: data.partner,
                                },
                            ],
                        },
                        {
                            value: data.partner.guid,
                            title: data.partner.name,
                            partner: data.partner,
                        },
                    ];
                    newList = [...partnersSelectList, ...newPartnerType];
                }
                await dispatch(paymentActions.updatePartnerList(newList));
                await setMainForm((prevState) => ({ ...prevState, partners: data.partner.guid }));
                await onShowModal();
            }
            if (data.errors) {
                const initErrors = {
                    type: null,
                    email: null,
                    name: null,
                };
                data.errors.forEach((error) => {
                    initErrors[error.name] = error.messages.toString();
                });
                setErrors(initErrors);
                loader.hide();
            }
        };
        fetchCreatePartner(formData, callback);
    };

    const selectHandler = (type) => {
        setFormState((prevState) => ({ ...prevState, type }));
    };
    const formHandler = (e) => {
        const { name, value } = e.target;
        setFormState((prevState) => ({ ...prevState, [name]: value }));
    };

    const dadataHandler = (data) => {
        setFormState((prev) => ({
            ...prev,
            fullName: data.full_name,
            inn: data.inn,
            kpp: data.kpp,
        }));

        setDadataList([]);
    };
    const selectedPartner = options.find((item) => item.value === formState.type);

    return (
        <ModalContext.Provider value={{}}>
            <CustomModal isOpen={isOpen} onHideModal={onShowModal}>
                <CustomModal.Header>{t('AddPartnerModal.Modal.ModalHeader')}</CustomModal.Header>
                <CustomModal.Body>
                    <Form>
                        <div className={classNames(cls.formBlock)}>
                            <FormBlock>
                                <Select
                                    isSmartPosition
                                    selected={selectedPartner || null}
                                    onChange={selectHandler}
                                    placeholder={t('AddPartnerModal.Form.Select.placeholder')}
                                    title={t('AddPartnerModal.Form.Select.title')}
                                    isRequired
                                    options={options}
                                    error={errors.type}
                                />
                                <Input
                                    isRequired
                                    title={t('AddPartnerModal.Form.Input.name.title')}
                                    name="name"
                                    value={formState.name}
                                    onChange={(e) => formHandler(e)}
                                    error={errors.name}
                                />
                            </FormBlock>
                            <FormBlock>
                                <Input
                                    isRequired
                                    title="E-mail"
                                    name="email"
                                    value={formState.email}
                                    onChange={(e) => formHandler(e)}
                                    error={errors.email}
                                />
                                <Input
                                    isRequired={false}
                                    title={t('AddPartnerModal.Form.Input.phone.title')}
                                    name="phone"
                                    value={formState.phone}
                                    onChange={(e) => formHandler(e)}
                                    // error={errors.sum}
                                />
                            </FormBlock>
                            <div className={classNames(cls.blockHeader)}>
                                {t('AddPartnerModal.return.blockHeader')}
                            </div>
                            <FormBlock>
                                <Input
                                    isRequired
                                    title={t('AddPartnerModal.Form.Input.fullName.title')}
                                    name="fullName"
                                    value={formState.fullName}
                                    rightIcon={isSearchable && icons.search}
                                    onChange={(e) => fullNameHandler(e)}
                                    // error={errors.sum}
                                    isDadata={isSearchable}
                                    dadataList={dadataList}
                                    setDatalist={setDadataList}
                                    dadataHandler={dadataHandler}
                                />
                            </FormBlock>
                            <FormBlock>
                                <Input
                                    isRequired={false}
                                    title={t('AddPartnerModal.Form.Input.inn.title')}
                                    name="inn"
                                    value={formState.inn}
                                    onChange={(e) => formHandler(e)}
                                />
                                <Input
                                    isRequired={false}
                                    title={t('AddPartnerModal.Form.Input.kpp.title')}
                                    name="kpp"
                                    value={formState.kpp}
                                    onChange={(e) => formHandler(e)}
                                    // error={errors.sum}
                                />
                            </FormBlock>

                            <FormBlock>
                                <Input
                                    isRequired={false}
                                    title={t('AddPartnerModal.Form.Input.bik.title')}
                                    name="bik"
                                    value={formState.bik}
                                    onChange={(e) => formHandler(e)}
                                    // error={errors.sum}
                                />
                                <Input
                                    isRequired={false}
                                    title={t(
                                        'AddPartnerModal.Form.Input.currentAccountNumber.title'
                                    )}
                                    name="currentAccountNumber"
                                    value={formState.currentAccountNumber}
                                    onChange={(e) => formHandler(e)}
                                    // error={errors.sum}
                                />
                            </FormBlock>
                        </div>
                    </Form>
                </CustomModal.Body>
                <CustomModal.Footer>
                    <Flex fullWidth justify="between">
                        <UiButton
                            onClick={onShowModal}
                            btnColor="secondary"
                            btnText={t('AddPartnerModal.ModalFooter.Button.cancel')}
                        />
                        <UiButton
                            onClick={onSubmitButton}
                            btnText={t('AddPartnerModal.ModalFooter.Button.save')}
                        />
                    </Flex>
                </CustomModal.Footer>
            </CustomModal>
        </ModalContext.Provider>
    );
};
